package app.footyguide.web.data

/**
 * Repositório de canais — Firestore SDK + localStorage cache.
 */
class WebChannelRepository {
    data class ChannelData(
        val id: String,
        val name: String,
        val type: String,
        val description: String,
        val affiliateLink: String?,
        val logo: String?,
    )

    private var memCache: Map<String, ChannelData>? = null

    internal fun fallbackChannels(): Map<String, ChannelData> = mapOf(
        "globo" to ChannelData("globo", "Globo", "tv", "TV Aberta", null, "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/channels/globo.webp?alt=media"),
        "sportv" to ChannelData("sportv", "SporTV", "tv", "Canal por assinatura", null, "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/channels/sportv.webp?alt=media"),
        "premiere" to ChannelData("premiere", "Premiere", "tv", "Pay-per-view", null, "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/channels/premiere.webp?alt=media"),
        "cazetv" to ChannelData("cazetv", "CazéTV", "streaming", "YouTube / Twitch", null, "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/channels/cazetv.webp?alt=media"),
        "prime-video" to ChannelData("prime-video", "Prime Video", "streaming", "Amazon Prime Video", null, "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/channels/prime-video.webp?alt=media"),
        "espn" to ChannelData("espn", "ESPN", "tv", "Canal por assinatura", null, "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/channels/espn.webp?alt=media"),
        "disney-plus" to ChannelData("disney-plus", "Disney+", "streaming", "Streaming esportivo", null, "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/channels/disney-plus.webp?alt=media"),
    )

    suspend fun getAll(): Map<String, ChannelData> {
        memCache?.let { return it }

        val cached = LocalCache.get(CACHE_KEY)
        if (cached != null) {
            val result = parseFromJson(cached)
            if (result.isNotEmpty()) {
                memCache = result
                return result
            }
        }

        return try {
            val docs = FirestoreSdk.collection("channels")
            if (docs.isEmpty()) {
                val fallback = fallbackChannels()
                memCache = fallback
                return fallback
            }
            val result = docs.associate { doc ->
                doc.id to ChannelData(
                    id = doc.id,
                    name = doc.string("name") ?: doc.id,
                    type = doc.string("type") ?: "tv",
                    description = doc.string("description") ?: "",
                    affiliateLink = doc.string("affiliateLink"),
                    logo = doc.string("logo")?.takeIf { it.isNotBlank() },
                )
            }
            memCache = result
            LocalCache.set(CACHE_KEY, serializeToJson(result))
            result
        } catch (_: Exception) {
            val fallback = fallbackChannels()
            memCache = fallback
            fallback
        }
    }

    suspend fun getChannel(channelId: String): ChannelData? {
        return getAll()[channelId]
    }

    private fun serializeToJson(items: Map<String, ChannelData>): String {
        val entries = items.entries.joinToString(",") { (id, c) ->
            fun esc(s: String) = s.replace("\"", "\\\"")
            val logoStr = c.logo?.let { """"${esc(it)}"""" } ?: "null"
            val affStr = c.affiliateLink?.let { """"${esc(it)}"""" } ?: "null"
            """{"id":"$id","name":"${esc(c.name)}","type":"${c.type}","desc":"${esc(c.description)}","aff":$affStr,"logo":$logoStr}"""
        }
        return "[$entries]"
    }

    private fun parseFromJson(json: String): Map<String, ChannelData> {
        return try {
            val arr = JSON.parse<dynamic>(json)
            val length = (arr.length as? Number)?.toInt() ?: 0
            (0 until length).mapNotNull { i ->
                val item = arr[i]
                val id = item.id as? String ?: return@mapNotNull null
                id to ChannelData(
                    id = id,
                    name = item.name as? String ?: id,
                    type = item.type as? String ?: "tv",
                    description = item.desc as? String ?: "",
                    affiliateLink = item.aff as? String,
                    logo = item.logo as? String,
                )
            }.toMap()
        } catch (_: Exception) { emptyMap() }
    }

    companion object {
        private const val CACHE_KEY = "fg-cache-channels"
    }
}
